/**
 * @module Shop
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */
// Core Libraries
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot } from '@angular/router';
import { Observable, BehaviorSubject, Subject } from 'rxjs';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
// import { SETTINGS } from '@environment/settings';
import { CommonUtilService } from '@support/shared/services/common-util.service';
import { HttpClient } from '@angular/common/http';
import { WEB_COMPONENT_CONSTANTS } from '@root/web/web.component.constants';
import {
  GeoLocationPositionInterface,
  GeoLocationZipCodeInterface,
  GeoCodingFeature,
} from '@experience/shop/shared-browse/interfaces/zip-code-entry-change.interface';
import { AppConfig } from '@root/app.config';
import { SharedBrowseService } from '../shared-browse/services/shared-browse.service';
import { Address } from '@support/api/proxies/responses/api-2004.interface';

/**
 * Route guard checks if cart token has been generated, if not requests for token
 */
@Injectable()
export class ZipCodeRequiredGuardService implements CanActivate {
  constructor(
    private stateStoreUtils: StateStoreUtilsService,
    private objectService: ObjectService,
    private sharedBrowseService: SharedBrowseService,
    private appConfig: AppConfig,
    private commonUtilService: CommonUtilService,
    private http: HttpClient
  ) {
    this.sharedBrowseService.zipCodeNotAvailableSource = new BehaviorSubject(false);
  }
  /**
   * Can Activate guard checks to make sure token/cartId is set
   */
  public canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    this.stateStoreUtils.setNextUrl(state.url);
    const isAnon = this.sharedBrowseService.isAnon();
    const subject = new Subject<boolean>();
    const defaultBrowseZipCode = this.sharedBrowseService.getDefaultZipCode();
    const anonBrowseAddress: Address = this.stateStoreUtils.getBrowseAddress();
    if (!anonBrowseAddress.postalCode && isAnon && defaultBrowseZipCode.postalCode) {
      this.sharedBrowseService.setZipCode(null, true, true, subject);
      return subject;
    } else {
      const address: Address = isAnon
        ? anonBrowseAddress
        : this.stateStoreUtils.getAuthBillingAddress();
      if (address.postalCode) {
        this.sharedBrowseService.updateBrowseZipCode(address, subject);
        return true;
      } else {
        const disableGeoLocation = this.appConfig.getConfig('disableGeoLocation');
        if (isAnon) {
          const storedZipCode = this.sharedBrowseService.getStoredZipCode();
          if (storedZipCode) {
            this.sharedBrowseService.setZipCode(storedZipCode, false, true, subject);
            return subject;
          } else {
            if (!disableGeoLocation && navigator.geolocation) {
              this.getGeoLocationForWeb(subject);
              return subject;
            } else {
              this.showZipModal();
              return subject;
            }
          }
        } else {
          return true;
        }
      }
    }
  }
  /**
   * Function to identify geo location and get Zipcode if its WEB channel
   * @param subject - flag to determine status of subject
   * @return {void}
   *
   */
  private getGeoLocationForWeb(subject?: Subject<boolean>): void {
    navigator.geolocation.getCurrentPosition(
      (position: GeoLocationPositionInterface) => {
        const currentLatitude: string = position.coords.latitude.toString();
        const currentLongitude: string = position.coords.longitude.toString();
        const tmoKitToken = this.objectService.get(
          this.appConfig.getConfig('coverageMap') || {},
          'config.map.access_token'
        );
        const getURLPathFromConfig =
          this.appConfig.getConfig('geoLocationUrl') || WEB_COMPONENT_CONSTANTS.WEB_GEO_LOCATION;
        const URL = this.commonUtilService.messageFormatter(getURLPathFromConfig, [
          currentLongitude,
          currentLatitude,
          tmoKitToken,
        ]);
        this.http.get(URL).subscribe(
          (data: GeoLocationZipCodeInterface) => {
            const postCodeFeature = data.features.find((feature: GeoCodingFeature) =>
              feature.place_type.includes('postcode')
            );
            if (postCodeFeature && postCodeFeature.text) {
              this.sharedBrowseService.setZipCode(postCodeFeature.text, false, true, subject);
            } else {
              this.showZipModal();
            }
          },
          () => {
            this.showZipModal();
          }
        );
      },
      () => {
        this.showZipModal();
      }
    );
  }

  /**
   * Function to remove key values from localstorage , set data and show ZIP Modal
   */
  private showZipModal(): void {
    this.sharedBrowseService.showZipModal();
  }
}
